import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Resident, User } from '../types';
import api from '../utils/api';
import ResidentRegistrationForm from '../components/ResidentRegistrationForm';
import ResidentDetailsModal from '../components/ResidentDetailsModal';
import CustomSelect from '../components/CustomSelect';
import RowActions from '../components/RowActions';

const Residents: React.FC = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [residents, setResidents] = useState<Resident[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageFilter, setAgeFilter] = useState('all');
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [editingResident, setEditingResident] = useState<Resident | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [availableStaff, setAvailableStaff] = useState<User[]>([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedResidentForAssignment, setSelectedResidentForAssignment] = useState<Resident | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedResidentForDetails, setSelectedResidentForDetails] = useState<Resident | null>(null);

  const fetchResidents = useCallback(async (page = 1, search = searchTerm, ageRange = ageFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(search && { search }),
        ...(ageRange !== 'all' && { ageRange })
      });

      const response = await api.get(`/residents?${params}`);
      
      if (response.data.success) {
        setResidents(response.data.data.residents);
        setCurrentPage(response.data.data.pagination.currentPage);
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalCount(response.data.data.pagination.totalCount);
      }
    } catch (err: any) {
      console.error('Error fetching residents:', err);
      setError(err.response?.data?.message || 'Failed to fetch residents');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, ageFilter]);

  useEffect(() => {
    fetchResidents();
  }, [fetchResidents]);

  // Handle navigation state messages
  useEffect(() => {
    const state = location.state as { message?: string; error?: string } | null;
    if (state?.message) {
      setMessage(state.message);
      // Clear the navigation state to prevent message from persisting
      window.history.replaceState({}, document.title);
    }
    if (state?.error) {
      setError(state.error);
      // Clear the navigation state to prevent error from persisting
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchResidents(1, searchTerm, ageFilter);
  };

  const handleAgeFilterChange = (filter: string) => {
    setAgeFilter(filter);
    setCurrentPage(1);
    fetchResidents(1, searchTerm, filter);
  };

  const handlePageChange = (page: number) => {
    fetchResidents(page, searchTerm, ageFilter);
  };

  const handleAddResident = () => {
    setEditingResident(null);
    setShowRegistrationModal(true);
  };

  const handleEditResident = (resident: Resident) => {
    setEditingResident(resident);
    setShowEditModal(true);
  };

  const handleViewDetails = (resident: Resident) => {
    setSelectedResidentForDetails(resident);
    setShowDetailsModal(true);
  };

  const handleRegistrationSuccess = (newResident: Resident) => {
    setShowRegistrationModal(false);
    setShowEditModal(false);
    setEditingResident(null);
    fetchResidents(currentPage, searchTerm, ageFilter);
  };

  const handleUpdateResident = async (residentData: any) => {
    if (!editingResident) return;

    try {
      const response = await api.put(`/residents/${editingResident.id}`, residentData);
      
      if (response.data.success) {
        setShowEditModal(false);
        setEditingResident(null);
        fetchResidents(currentPage, searchTerm, ageFilter);
      }
    } catch (err: any) {
      console.error('Error updating resident:', err);
      setError(err.response?.data?.message || 'Failed to update resident');
    }
  };

  const handleDeactivateResident = async (residentId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this resident?')) {
      return;
    }

    try {
      const response = await api.delete(`/residents/${residentId}`);
      
      if (response.data.success) {
        fetchResidents(currentPage, searchTerm, ageFilter);
      }
    } catch (err: any) {
      console.error('Error deactivating resident:', err);
      setError(err.response?.data?.message || 'Failed to deactivate resident');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAgeRangeDisplay = (age: number) => {
    if (age < 30) return 'Under 30';
    if (age <= 50) return '30-50';
    if (age <= 70) return '51-70';
    return 'Over 70';
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
        <p className="text-gray-600 mt-2">You don't have permission to manage residents.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Residents Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage residents and their information ({totalCount} total)
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <button
            type="button"
            onClick={handleAddResident}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <svg className="-ml-1 mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Resident
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow">
        <form onSubmit={handleSearch} className="md:flex md:items-center md:space-x-4">
          <div className="flex-1">
            <label htmlFor="search" className="sr-only">Search residents</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                id="search"
                name="search"
                type="text"
                placeholder="Search by name or diagnosis..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 input-mobile border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="mt-4 md:mt-0">
            <CustomSelect
              value={ageFilter}
              onChange={handleAgeFilterChange}
              options={[
                { value: 'all', label: 'All Ages' },
                { value: 'under-30', label: 'Under 30' },
                { value: '30-50', label: '30-50' },
                { value: '51-70', label: '51-70' },
                { value: 'over-70', label: 'Over 70' },
              ]}
            />
          </div>
          
          <button
            type="submit"
            className="mt-4 md:mt-0 px-4 py-2 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Search
          </button>
        </form>
      </div>

      {/* Success/Info Message */}
      {message && (
        <div className="rounded-md bg-blue-50 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Information</h3>
              <div className="mt-2 text-sm text-blue-700">{message}</div>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  type="button"
                  onClick={() => setMessage('')}
                  className="inline-flex bg-blue-50 rounded-md p-1.5 text-blue-500 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-blue-50 focus:ring-blue-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{error}</div>
            </div>
            <div className="ml-auto pl-3">
              <div className="-mx-1.5 -my-1.5">
                <button
                  type="button"
                  onClick={() => setError('')}
                  className="inline-flex bg-red-50 rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Residents Table */}
      <div className="bg-white card-list overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : residents.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No residents found</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding a new resident.</p>
            <div className="mt-6">
              <button
                type="button"
                onClick={handleAddResident}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-base font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <svg className="-ml-1 mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Add Resident
              </button>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {residents.map((resident) => (
              <li key={resident.id}>
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-12 w-12">
                        <img
                          className="h-12 w-12 rounded-full object-cover"
                          src={resident.photo || '/images/profiles/placeholder-resident.jpg'}
                          alt={resident.name}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="flex items-center">
                          <h3 className="text-lg font-medium text-gray-900">{resident.name}</h3>
                          <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 badge-pill ${
                            resident.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {resident.isActive ? 'Active' : 'Discharged'}
                          </span>
                        </div>
                        <div className="flex items-center mt-1 text-sm text-gray-500 flex-wrap gap-y-1">
                          <span>Age: {resident.age}</span>
                          {resident.dateOfBirth && (
                            <>
                              <span className="mx-2">•</span>
                              <span>DOB: {formatDate(resident.dateOfBirth)}</span>
                            </>
                          )}
                          <span className="mx-2">•</span>
                          <span>{getAgeRangeDisplay(resident.age)}</span>
                          <span className="mx-2">•</span>
                          <span>Admitted: {formatDate(resident.admissionDate)}</span>
                        </div>
                        <div className="mt-1 text-sm text-gray-600 max-w-2xl">
                          <strong>Diagnosis:</strong> {resident.diagnosis}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center">
                      <RowActions
                        actions={[
                          { label: 'View Details', tone: 'primary', onClick: () => handleViewDetails(resident) },
                          { label: 'Care Plan', tone: 'default', onClick: () => navigate(`/residents/${resident.id}/care-plan`) },
                          { label: 'Edit', tone: 'default', onClick: () => handleEditResident(resident) },
                          { label: 'Deactivate', tone: 'danger', onClick: () => handleDeactivateResident(resident.id) },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{((currentPage - 1) * 20) + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * 20, totalCount)}</span> of{' '}
                <span className="font-medium">{totalCount}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Registration Modal */}
      <ResidentRegistrationForm
        isOpen={showRegistrationModal}
        onClose={() => {
          setShowRegistrationModal(false);
          setEditingResident(null);
        }}
        onSuccess={handleRegistrationSuccess}
      />

      {/* Edit Modal */}
      {showEditModal && editingResident && (
        <ResidentEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditingResident(null);
          }}
          resident={editingResident}
          onUpdate={handleUpdateResident}
        />
      )}

      {/* Resident Details Modal */}
      <ResidentDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedResidentForDetails(null);
        }}
        resident={selectedResidentForDetails}
      />
    </div>
  );
};

// Edit Modal Component
interface ResidentEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  resident: Resident;
  onUpdate: (data: any) => void;
}

const ResidentEditModal: React.FC<ResidentEditModalProps> = ({
  isOpen,
  onClose,
  resident,
  onUpdate
}) => {
  const [formData, setFormData] = useState({
    name: resident.name,
    dateOfBirth: resident.dateOfBirth ? resident.dateOfBirth.split('T')[0] : '',
    age: resident.age.toString(),
    diagnosis: resident.diagnosis,
    photo: resident.photo || '',
    emergencyContact: resident.emergencyContact || {
      name: '',
      phone: '',
      relationship: ''
    },
    admissionDate: resident.admissionDate ? resident.admissionDate.split('T')[0] : '',
    isActive: resident.isActive
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('emergencyContact.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        emergencyContact: {
          ...prev.emergencyContact,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name || !formData.diagnosis) {
      setError('Please fill in all required fields');
      return;
    }

    if (!formData.dateOfBirth && !formData.age) {
      setError('Please provide either date of birth or age');
      return;
    }

    if (formData.age) {
      const age = parseInt(formData.age);
      if (isNaN(age) || age <= 0 || age > 150) {
        setError('Please enter a valid age between 1 and 150');
        return;
      }
    }

    setLoading(true);

    try {
      const emergencyContact = formData.emergencyContact.name ? {
        name: formData.emergencyContact.name,
        phone: formData.emergencyContact.phone,
        relationship: formData.emergencyContact.relationship
      } : null;

      await onUpdate({
        name: formData.name,
        dateOfBirth: formData.dateOfBirth || undefined,
        age: formData.age ? parseInt(formData.age) : undefined,
        diagnosis: formData.diagnosis,
        photo: formData.photo || null,
        emergencyContact: emergencyContact,
        admissionDate: formData.admissionDate,
        isActive: formData.isActive
      });
    } catch (err) {
      setError('Failed to update resident');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center pb-4 border-b border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900">Edit Resident</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="mt-1 block w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
              <input
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
              />
              {!formData.dateOfBirth && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-500">Age (if DOB not available)</label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    min="1"
                    max="150"
                    className="mt-1 block w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Medical Diagnosis *</label>
            <textarea
              name="diagnosis"
              value={formData.diagnosis}
              onChange={handleInputChange}
              required
              rows={3}
              className="mt-1 block w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Photo URL</label>
            <input
              type="url"
              name="photo"
              value={formData.photo}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Admission Date</label>
            <input
              type="date"
              name="admissionDate"
              value={formData.admissionDate}
              onChange={handleInputChange}
              className="mt-1 block w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Emergency Contact</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  name="emergencyContact.name"
                  value={formData.emergencyContact.name}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Phone</label>
                <input
                  type="tel"
                  name="emergencyContact.phone"
                  value={formData.emergencyContact.phone}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Relationship</label>
                <input
                  type="text"
                  name="emergencyContact.relationship"
                  value={formData.emergencyContact.relationship}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 input-mobile border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-primary-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Residents;